import React, { useState } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData, View } from 'react-native';
import moment from 'moment';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { useQuery } from '@tanstack/react-query';
import MyDataTable from '../../../components/MyDataTable';
import MyAppText from '../../../components/MyAppText';
import { Pagination } from '../../../components/Pagination';
import FilterInput from '../../../components/Filter/components/FilterInput';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { translate } from '../../../services/translate';
import { accessService } from '../../../services/central-api/access';
import { Action } from '../../../services/central-api/audit-logs';
import UserHistoricFilter, { UserHistoricFiltersParams } from './UserHistoricFilters';

interface Props {
    userId: number;
}

export default function UserHistoricList({ userId }: Props) {
    const { styles } = useStyles(styleSheet);

    const [filters, setFilters] = useState<UserHistoricFiltersParams>({
        page: 0,
        limit: 25,
    });

    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');

    function getActionsMatchingText(text?: string) {
        if (!text) {
            return undefined;
        }
        const normalize = (value: string) => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
        const search = normalize(text.trim());
        return Object.values(Action).filter(action =>
            normalize(translate(action)).includes(search) || normalize(action).includes(search)
        );
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    const { data, isLoading } = useQuery({
        queryKey: ['userHistoricList', userId, filters],
        queryFn: () => accessService.getUserHistory({
            userId,
            sort: 'desc',
            ...filters,
            actions: getActionsMatchingText(filters.textFilter),
        }),
        enabled: userId > 0,
    });

    function formatDetailValue(key: string, value: unknown) {
        if (/date/i.test(key) && value) {
            return moment(value as string | number | Date).format('DD/MM/YYYY HH:mm:ss');
        }

        if (typeof value === 'object') {
            return JSON.stringify(value);
        }

        return String(value);
    }

    const totalItems = (data ?? []).length === filters.limit
        ? (filters.page + 2) * filters.limit
        : filters.page * filters.limit + (data ?? []).length;

    return (
        <View style={styles.container}>
            <DataTableHeader
                zIndex={2}
                title={translate('userHistoric')}
                filter={<UserHistoricFilter
                    userId={userId}
                    filters={filters}
                    setFilters={setFilters}
                    textFilter={textFilter}
                    setTextFilter={setTextFilter} />}
                filterInput={<FilterInput
                    value={textFilter}
                    onChange={setTextFilter}
                    placeholder={translate('typeToSearch')}
                    onKeyPress={handleKeyDown}
                />}
            />
            <DataTableContainer
                dataTable={<MyDataTable
                    columns={[
                        {
                            name: translate('date'),
                            selector: row => moment(row.date).format('DD/MM/YYYY HH:mm:ss'),
                            wrap: true,
                        },
                        {
                            name: translate('responsible'),
                            selector: row =>
                                row.userId === -1 && row.userName
                                    ? translate(row.userName)
                                    : row.userId
                                        ? `${row.userId} - ${row.userName}`
                                        : translate('notApplicable'),
                            wrap: true,
                        },
                        {
                            name: translate('ipAddress'),
                            selector: row => row.ip ?? '',
                        },
                        {
                            name: translate('action'),
                            selector: row => translate(row.action),
                            wrap: true,
                        },
                    ]}
                    data={data ?? []}
                    expandableRows
                    expandableRowsComponent={({ data: log }) => (
                        <View style={styles.detailTable}>
                            {Object.entries(log.detail ?? {})
                                .filter(([k]) => k !== 'company' && k !== 'companyId')
                                .map(([k, v]) => (
                                    <View key={k} style={styles.detailRow}>
                                        <MyAppText style={styles.detailKey}>{k}</MyAppText>
                                        <MyAppText style={styles.detailValue}>
                                            {formatDetailValue(k, v)}
                                        </MyAppText>
                                    </View>
                                ))}
                        </View>
                    )}
                    progressPending={isLoading}
                />}
                pagination={<Pagination
                    totalItems={totalItems}
                    currentPage={filters.page}
                    pageSize={filters.limit}
                    setPage={page => setFilters(old => ({ ...old, page }))}
                    setPageSize={pageSize => setFilters(old => ({ ...old, limit: pageSize, page: 0 }))}
                    hideTotalItemsAndPagesInfo={true}
                />}
            />
        </View>
    );
}

const styleSheet = createStyleSheet(theme => ({
    container: {
        flex: 1,
        gap: 8,
    },
    detailTable: {
        backgroundColor: theme.colors.container.background,
        padding: 8,
        flex: 1,
    },
    detailRow: {
        flexDirection: 'row',
        paddingVertical: 4,
        borderBottomWidth: 1,
        borderColor: theme.colors.card.border,
    },
    detailKey: {
        flex: 1,
        fontSize: 12,
        fontWeight: '600',
    },
    detailValue: {
        flex: 2,
        fontSize: 12,
        maxWidth: 700,
    },
}));
